// src/pages/admin/AdminReservations.tsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Video, CreditCard, ChevronDown, Mail, Smartphone, QrCode, Copy } from 'lucide-react';
import { FaComment, FaEnvelope, FaQrcode, FaMobileAlt } from 'react-icons/fa';

type ReservationStatus =
  | 'pending'
  | 'assigned'
  | 'reserved'
  | 'active'
  | 'completed'
  | 'canceled'
  | 'doctor_cancelled'
  | 'admin_cancelled'
  | 'expired';

type PaymentStatus = 'paid' | 'unpaid' | 'failed' | null | undefined;

interface Reservation {
  id: number;
  user_email: string | null;
  user_name?: string | null;
  user_provider?: string | null;
  patient_chart_number?: string | null;
  service: string;
  visit_type?: string | null;
  date: string; // 'YYYY-MM-DD'
  time: string; // 'HH:mm'
  status: ReservationStatus;
  needs_translation?: boolean;
  is_overdue?: boolean;
  questionnaire_completed: boolean;

  diagnosis?: string | null;
  prescription_text?: string | null;
  notes?: string | null;

  zoom_join_url: string | null;
  zoom_start_url?: string | null;
  zoom_meeting_id?: string | null;
  meeting_url?: string | null;

  doctor_name?: string | null;
  doctor_id?: number | null;

  payment_status?: PaymentStatus;
  total_amount_jpy?: number | null;
  transaction_id?: string | null;
  receipt_url?: string | null;

  user_profile?: {
    full_name?: string | null;
    furigana?: string | null;
    gender?: string | null;
    birth_date?: string | null;
    phone_number?: string | null;
    postal_code?: string | null;
    address?: string | null;
    karte_number?: string | null;
    chart_number?: string | null;
    medical_record_number?: string | null;
    karteNo?: string | null;
    mrn?: string | null;
    patient_code?: string | null;
    patient_id?: string | null;
  };

  shipping_status?: string | null;
  tracking_number?: string | null;
  shipped_at?: string | null;
  shipping_carrier?: string | null;
}

const STORAGE_KEY_SORT = 'adminReservations.sortOrder';
const STORAGE_KEY_PAGESIZE = 'adminReservations.pageSize';
const STORAGE_KEY_COLLAPSE = 'adminReservations.collapsedGroups';
const STORAGE_KEY_PAGEBY = 'adminReservations.pageByGroup';

const safeParseJSON = <T,>(s: string | null): T | null => {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
};

const EXPIRY_GRACE_MINUTES = 60;

const AdminReservations: React.FC = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pageByGroup, setPageByGroup] = useState<Record<string, number>>({});
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState(false);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState<Reservation | null>(null);

  const [showRecordModal, setShowRecordModal] = useState(false);
  const [recordTarget, setRecordTarget] = useState<Reservation | null>(null);
  const [expandedActionRows, setExpandedActionRows] = useState<Record<number, boolean>>({});

  const toggleActionRow = (id: number) => {
    setExpandedActionRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // 配送・CSV関連
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvCoolType, setCsvCoolType] = useState<number>(0); // 0:Normal, 1:Cool(Fridge), 2:Cool(Frozen)
  const [csvShipDate, setCsvShipDate] = useState<string>(''); // YYYY-MM-DD

  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingTarget, setTrackingTarget] = useState<Reservation | null>(null);
  const [trackingInput, setTrackingInput] = useState('');
  // 配送業者（ヤマト/佐川/日本郵便）。配送管理画面と同じく選択式にする。
  const [trackingCarrier, setTrackingCarrier] = useState('yamato');

  // 発送元住所
  const [senderAddresses, setSenderAddresses] = useState<any[]>([]);
  const [selectedSenderId, setSelectedSenderId] = useState<number | undefined>(undefined);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const handleCopyLink = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      alert(`${label} ${t('copied') || 'をコピーしました'}\n${text}`);
    }).catch(() => {
      alert(t('copy_failed') || 'コピーに失敗しました');
    });
  };

  const renderProviderIcon = (provider?: string | null) => {
    const p = (provider || 'email').toLowerCase();
    switch (p) {
      case 'wechat':
        return <FaComment className="text-emerald-500" size={14} title="WeChat" />;
      case 'line':
        return <FaComment className="text-[#00B900]" size={14} title="LINE" />;
      case 'phone':
        return <FaMobileAlt className="text-orange-500" size={14} title="Phone" />;
      case 'manual':
        return <FaQrcode className="text-amber-500" size={14} title="QR/Manual" />;
      default:
        return <FaEnvelope className="text-slate-400" size={14} title="Email" />;
    }
  };

  const parseDateTime = (dateStr: string, timeStr: string): number => {
    const ds = dateStr.replace(/\./g, '/').replace(/-/g, '/');
    const [y, m, d] = ds.split('/').map(Number);
    const [hh, mm] = timeStr.split(':').map(Number);
    if (!y || !m || !d || isNaN(hh) || isNaN(mm)) return 0;
    return new Date(y, m - 1, d, hh, mm, 0, 0).getTime();
  };

  // ✅ CSV出力対象：完了済み かつ 支払い済み
  const isCsvEligible = (r: Reservation) => r.status === 'completed' && r.payment_status === 'paid';

  // カルテ番号として使用できそうな候補から最初の値を取得
  const getKarteNumber = (r: Reservation): string | null => {
    const p = r.user_profile || {};
    const candidates = [
      r.patient_chart_number,
      (p as any).karte_number,
      (p as any).chart_number,
      (p as any).medical_record_number,
      (p as any).karteNo,
      (p as any).mrn,
      (p as any).patient_code,
      (p as any).patient_id,
    ];
    const first = candidates.find((v) => typeof v === 'string' && v.trim().length > 0);
    return first || null;
  };

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/reservations', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setReservations(res.data as Reservation[]);

      // ✅ 念のため：対象外が選択されていたら落とす
      setSelectedIds((prev) => {
        const allowed = new Set<number>();
        for (const id of prev) {
          const r = (res.data as Reservation[]).find((x) => x.id === id);
          if (r && isCsvEligible(r)) allowed.add(id);
        }
        return allowed;
      });
    } catch (err) {
      console.error('Error fetching reservations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token) {
      navigate('/login/admin');
      return;
    }
    if (role !== 'admin') {
      alert(t('admin.common.access_denied'));
      navigate('/');
      return;
    }

    const params = new URLSearchParams(location.search);
    const urlSort = params.get('sort');
    const urlPs = params.get('ps');
    const urlCg = params.get('cg');
    const urlPg = params.get('pg');

    let effectiveSort: 'asc' | 'desc' =
      urlSort === 'asc' || urlSort === 'desc'
        ? urlSort
        : ((localStorage.getItem(STORAGE_KEY_SORT) as any) || 'desc');

    let effectivePs = urlPs ? Number(urlPs) : Number(localStorage.getItem(STORAGE_KEY_PAGESIZE)) || 20;
    if (![10, 20, 50].includes(effectivePs)) effectivePs = 20;

    const effectiveCg =
      safeParseJSON<Record<string, boolean>>(urlCg) ??
      safeParseJSON<Record<string, boolean>>(localStorage.getItem(STORAGE_KEY_COLLAPSE)) ??
      {};
    const effectivePg =
      safeParseJSON<Record<string, number>>(urlPg) ??
      safeParseJSON<Record<string, number>>(localStorage.getItem(STORAGE_KEY_PAGEBY)) ??
      {};

    setSortOrder(effectiveSort);
    setPageSize(effectivePs);
    setCollapsedGroups(effectiveCg);
    setPageByGroup(effectivePg);

    let needInject = false;
    if (params.get('sort') !== effectiveSort) needInject = true;
    if (params.get('ps') !== String(effectivePs)) needInject = true;
    if (params.get('cg') !== JSON.stringify(effectiveCg)) needInject = true;
    if (params.get('pg') !== JSON.stringify(effectivePg)) needInject = true;
    if (needInject) {
      params.set('sort', effectiveSort);
      params.set('ps', String(effectivePs));
      params.set('cg', JSON.stringify(effectiveCg));
      params.set('pg', JSON.stringify(effectivePg));
      navigate({ search: params.toString() }, { replace: true });
    }

    fetchReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SORT, sortOrder);
    const params = new URLSearchParams(location.search);
    if (params.get('sort') !== sortOrder) {
      params.set('sort', sortOrder);
      if (!params.get('ps')) params.set('ps', String(pageSize));
      if (!params.get('cg')) params.set('cg', JSON.stringify(collapsedGroups));
      if (!params.get('pg')) params.set('pg', JSON.stringify(pageByGroup));
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOrder]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_PAGESIZE, String(pageSize));
    const params = new URLSearchParams(location.search);
    if (params.get('ps') !== String(pageSize)) {
      params.set('ps', String(pageSize));
      if (!params.get('sort')) params.set('sort', sortOrder);
      if (!params.get('cg')) params.set('cg', JSON.stringify(collapsedGroups));
      if (!params.get('pg')) params.set('pg', JSON.stringify(pageByGroup));
      navigate({ search: params.toString() }, { replace: true });
    }
    setPageByGroup({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_COLLAPSE, JSON.stringify(collapsedGroups));
    const params = new URLSearchParams(location.search);
    const next = JSON.stringify(collapsedGroups);
    if (params.get('cg') !== next) {
      params.set('cg', next);
      if (!params.get('sort')) params.set('sort', sortOrder);
      if (!params.get('ps')) params.set('ps', String(pageSize));
      if (!params.get('pg')) params.set('pg', JSON.stringify(pageByGroup));
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsedGroups]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_PAGEBY, JSON.stringify(pageByGroup));
    const params = new URLSearchParams(location.search);
    const next = JSON.stringify(pageByGroup);
    if (params.get('pg') !== next) {
      params.set('pg', next);
      if (!params.get('sort')) params.set('sort', sortOrder);
      if (!params.get('ps')) params.set('ps', String(pageSize));
      if (!params.get('cg')) params.set('cg', JSON.stringify(collapsedGroups));
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageByGroup]);

  const sortedReservations = useMemo(() => {
    const arr = [...reservations];
    arr.sort((a, b) => {
      const ta = parseDateTime(a.date, a.time);
      const tb = parseDateTime(b.date, b.time);
      return sortOrder === 'asc' ? ta - tb : tb - ta;
    });
    return arr;
  }, [reservations, sortOrder]);

  const grouped: Record<string, Reservation[]> = useMemo(
    () => ({
      pool: sortedReservations.filter((r) => r.status === 'pending'),
      assigned: sortedReservations.filter((r) => ['assigned', 'reserved', 'active'].includes(r.status)),
      completed: sortedReservations.filter((r) => r.status === 'completed'),
      canceled: sortedReservations.filter(
        (r) => r.status === 'canceled' || r.status === 'doctor_cancelled' || r.status === 'admin_cancelled'
      ),
      expired: sortedReservations.filter((r) => r.status === 'expired'),
    }),
    [sortedReservations]
  );

  const renderStatusLabel = (r: Reservation) => {
    const { status, is_overdue } = r;

    // 1. 过期处理
    if (status === 'expired') {
      return <span className="text-red-500 font-semibold">{t('status.expired') || '有効期限切れ'}</span>;
    }
    
    // 2. 区分处理不同类型的取消
    if (status === 'canceled') {
      return <span className="text-gray-400 font-medium">{t('status.canceled') || '患者キャンセル'}</span>;
    }
    if (status === 'doctor_cancelled') {
      return <span className="text-orange-500 font-medium">{t('status.doctor_cancelled') || '医師キャンセル'}</span>;
    }
    if (status === 'admin_cancelled') {
      return <span className="text-red-400 font-medium">{t('status.admin_cancelled') || '管理キャンセル'}</span>;
    }

    // 3. 完成处理
    if (status === 'completed') {
      return <span className="text-emerald-600 font-semibold">{t('status.completed') || '完了'}</span>;
    }

    // 4. 超期提醒
    if (is_overdue) {
      const label = status === 'pending' ? t('status.pending') : t('status.assigned');
      return (
        <div className="flex flex-col items-center">
          <span className="text-red-500 font-bold animate-pulse">
            {label} ({t('status.overdue_short')})
          </span>
        </div>
      );
    }

    // 5. 其他常规状态
    switch (status) {
      case 'pending':
        return <span className="text-amber-500 font-semibold">{t('status.pending') || '未割当'}</span>;
      case 'assigned':
      case 'reserved':
      case 'active':
        return <span className="text-coral font-semibold">{t('status.assigned') || '診察待ち'}</span>;
      default:
        return <span className="text-gray-500">{t(`status.${status}` as any) || status}</span>;
    }
  };

  const yen = (n?: number | null) => (typeof n === 'number' ? `${n.toLocaleString()}円` : '');

  const renderPayment = (r: Reservation) => {
    const ps = r.payment_status;
    if (ps === 'paid') {
      return (
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-emerald-700 font-semibold">{t('payment.paid') || '支払済'}</span>
          {typeof r.total_amount_jpy === 'number' && (
            <span className="text-[12px] text-gray-600">
              {yen(r.total_amount_jpy)} {t('admin.tax_included')}
            </span>
          )}
          <button
            onClick={async () => {
              // User requested "previous viewing link" behavior.
              // Direct open Stripe Web/PDF link.
              if (r.receipt_url) {
                window.open(r.receipt_url, '_blank');
                return;
              }
              try {
                const res = await api.get(`/receipts/pdf/${r.id}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Receipt-${r.id}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
              } catch (e) {
                alert(t('payment.receipt_open_failed') || '領収書を開くことができませんでした');
              }
            }}
            className="text-[12px] text-coral underline hover:text-navy bg-transparent border-none cursor-pointer"
          >
            {t('payment.receipt') || '領収書'}
          </button>
        </div>
      );
    }
    if (ps === 'failed') return <span className="text-red-600 font-semibold">{t('payment.failed') || '失败'}</span>;
    const isUnpaid = ps === 'unpaid' || (r.status === 'completed' && r.payment_status !== 'paid');

    if (isUnpaid) {
      return (
        <div className="flex items-center justify-center gap-1.5 group">
          <span className="text-orange-600 font-semibold">{t('payment.unpaid') || '未结算'}</span>
          <button
            onClick={() => handleCopyLink(`${window.location.origin}/payment/start/${r.id}`, t('payment.link') || '结算链接')}
            className="p-1.5 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 transition-all active:scale-95"
            title={t('admin.copy_payment_link') || '复制支付链接'}
          >
            <Copy size={13} />
          </button>
        </div>
      );
    }
    if (['assigned', 'reserved', 'active'].includes(r.status))
      return <span className="text-gray-600">{t('payment.before_visit') || '診療前'}</span>;
    if (r.status === 'expired') return <span className="text-red-500">{t('mypage.expired') || '有効期限切れ'}</span>;
    if (['canceled', 'doctor_cancelled', 'admin_cancelled'].includes(r.status)) return <span className="text-gray-400">—</span>;
    if (r.status === 'pending') return <span className="text-gray-600">{t('doctor.pool') || '予約プール'}</span>;
    return <span className="text-gray-400">—</span>;
  };

  const renderTranslationNeedText = (r: Reservation): string => {
    return r.needs_translation
      ? (t('reserve.translation_required_yes') || '需要中文翻译')
      : (t('reserve.translation_required_no') || '不需要翻译');
  };

  // 管理者キャンセル
  const adminCancel = async (rid: number, status?: string) => {
    // 診療完了済みは誤操作防止のため、より明確な確認文言を出す（未払いのみ取消可）
    const confirmMsg = status === 'completed'
      ? (t('admin.confirm_cancel_completed') || 'この予約は「診療完了」済みです（未払い）。本当にキャンセルしますか？')
      : (t('admin.confirm_cancel_reservation') || 'この予約をキャンセルしますか？');
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.patch(
        `/admin/reservations/${rid}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setReservations((prev) => prev.map((r) => (r.id === rid ? ({ ...r, status: 'admin_cancelled' } as Reservation) : r)));
      // ✅ 選択対象外になる可能性があるので落とす
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(rid);
        return next;
      });
      alert(t('admin.cancel_success') || 'キャンセルしました。');
    } catch (e) {
      console.error(e);
      alert(t('admin.cancel_failed') || 'キャンセルに失敗しました。');
    }
  };

  // 管理者による「完了」マーク
  const adminMarkCompleted = async (rid: number) => {
    if (!window.confirm(t('admin.confirm_complete') || 'この予約を完了としてマークしますか？')) return;
    try {
      await api.patch(
        `/admin/reservations/${rid}/complete`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setReservations((prev) => prev.map((r) => (r.id === rid ? { ...r, status: 'completed' } : r)));
      alert(t('admin.complete_success') || '完了にしました。');
    } catch (e) {
      console.error(e);
      alert(t('admin.complete_failed') || '完了マークに失敗しました。');
    }
  };

  // ✅ 対象外は選択できない
  const toggleSelect = (r: Reservation) => {
    if (!isCsvEligible(r)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(r.id)) next.delete(r.id);
      else next.add(r.id);
      return next;
    });
  };

  // ✅ 複数IDをまとめて選択/解除（対象のみ）
  const toggleSelectAllIds = (ids: number[], select: boolean) => {
    if (ids.length === 0) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (select) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const selectedEligibleCount = useMemo(() => {
    // selectedIds は fetchReservations 時に対象外を落とすが、念のため二重で集計
    let c = 0;
    for (const id of selectedIds) {
      const r = reservations.find((x) => x.id === id);
      if (r && isCsvEligible(r)) c++;
    }
    return c;
  }, [selectedIds, reservations]);

  const handleGenerateCsv = async () => {
    // ✅ 二次フィルタ（安全策）
    const eligibleIds = Array.from(selectedIds).filter((id) => {
      const r = reservations.find((x) => x.id === id);
      return !!r && isCsvEligible(r);
    });
    if (eligibleIds.length === 0) {
      alert(t('admin.csv_no_eligible') || 'CSV出力できる対象（完了済み & 支払済み）が選択されていません。');
      return;
    }

    try {
      const res = await api.post(
        '/admin/reservations/csv/yamato',
        {
          reservation_ids: eligibleIds,
          cool_type: csvCoolType,
          sender_address_id: selectedSenderId,
          ship_date: csvShipDate,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob',
        }
      );

      const dateStr = new Date().toISOString().slice(0, 10);
      let fileName = `yamato_export_${dateStr}.csv`;
      if (eligibleIds.length === 1) {
        const r = reservations.find((x) => x.id === eligibleIds[0]);
        if (r && r.user_name) {
          fileName = `yamato_export_${dateStr}${r.user_name}様.csv`;
        }
      } else if (eligibleIds.length > 1) {
        fileName = `yamato_export_${dateStr}_batch.csv`;
      }

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setShowCsvModal(false);
      setSelectedIds(new Set());
    } catch (e) {
      console.error(e);
      alert('CSV生成に失敗しました / Failed to generate CSV');
    }
  };

  const openCsvModal = async () => {
    // 明日をデフォルトに設定
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().slice(0, 10);
    setCsvShipDate(tomorrowStr);

    try {
      const resp = await api.get('/admin/sender-addresses');
      setSenderAddresses(resp.data);
      if (resp.data.length > 0 && selectedSenderId === undefined) {
        setSelectedSenderId(resp.data[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch sender addresses', err);
    }
    setShowCsvModal(true);
  };

  const openTrackingModal = (r: Reservation) => {
    setTrackingTarget(r);
    setTrackingInput(r.tracking_number || '');
    // 旧値 yu_pack は yupack に正規化して初期選択（未設定は yamato）
    setTrackingCarrier(r.shipping_carrier === 'yu_pack' ? 'yupack' : (r.shipping_carrier || 'yamato'));
    setShowTrackingModal(true);
  };

  const saveTrackingNumber = async () => {
    if (!trackingTarget) return;
    try {
      const res = await api.patch(
        `/admin/reservations/${trackingTarget.id}/tracking`,
        { tracking_number: trackingInput, shipping_carrier: trackingCarrier },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const updated = res.data as Reservation;
      setReservations((prev) => prev.map((r) => (r.id === trackingTarget.id ? updated : r)));
      setShowTrackingModal(false);
      alert(t('admin.shipping_update_success') || '追跡番号を登録しました');
    } catch (e) {
      console.error(e);
      alert(t('admin.shipping_update_failed') || '登録に失敗しました');
    }
  };

  const openRecordView = (r: Reservation) => {
    setRecordTarget(r);
    setShowRecordModal(true);
  };

  const renderDesktopTable = (group: string, list: Reservation[]) => {
    const rawCurrentPage = pageByGroup[group] || 1;
    const totalPages = Math.ceil(list.length / pageSize) || 1;
    const currentPage = Math.min(rawCurrentPage, totalPages);
    const startIndex = (currentPage - 1) * pageSize;
    const paginated = list.slice(startIndex, startIndex + pageSize);

    // ✅ このページ内でCSV対象になるID
    const eligibleIdsOnPage = paginated.filter(isCsvEligible).map((r) => r.id);
    const selectedEligibleOnPageCount = eligibleIdsOnPage.filter((id) => selectedIds.has(id)).length;
    const allEligibleOnPageSelected = eligibleIdsOnPage.length > 0 && selectedEligibleOnPageCount === eligibleIdsOnPage.length;
    const someEligibleOnPageSelected = selectedEligibleOnPageCount > 0 && selectedEligibleOnPageCount < eligibleIdsOnPage.length;

    return (
      <>
        <table className="hidden md:table min-w-full table-auto text-[14px]">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center">
              <th className="w-10 px-2">
                {/* ✅ 全選択（このページの「完了&支払済」だけ） */}
                <input
                  ref={(el) => {
                    if (!el) return;
                    el.indeterminate = someEligibleOnPageSelected;
                  }}
                  type="checkbox"
                  checked={allEligibleOnPageSelected}
                  disabled={eligibleIdsOnPage.length === 0}
                  onChange={(e) => {
                    toggleSelectAllIds(eligibleIdsOnPage, e.target.checked);
                  }}
                  className="w-4 h-4"
                  title={
                    eligibleIdsOnPage.length === 0
                      ? t('admin.csv_select_disabled') || 'このページにCSV対象（完了&支払済）がありません'
                      : t('admin.select_all_eligible') || 'このページのCSV対象を全選択'
                  }
                />
              </th>
              <th className="w-auto md:w-44">{t('doctor.patient')}</th>
              <th className="w-auto md:w-40">{t('doctor.service')}</th>
              <th className="w-28">{t('doctor.date')}</th>
              <th className="w-20">{t('doctor.time')}</th>
              <th className="w-28">{t('doctor.status')}</th>
              <th className="w-36">{t('reserve.translation_col_header') || '翻译需求'}</th>
              <th className="w-28">{t('payment.status') || '支払状態'}</th>
              <th className="w-28">{t('doctor.questionnaire')}</th>
              <th className="w-32">{t('doctor.profile')}</th>
              <th className="w-40">{t('doctor.medical_record')}</th>
              <th className="w-48">{t('admin.actions') || '操作'}</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((r) => {
              const canCancel = !['canceled', 'doctor_cancelled', 'admin_cancelled'].includes(r.status) && r.payment_status !== 'paid';
              const canComplete = r.status !== 'completed';
              const karte = getKarteNumber(r);

              const eligible = isCsvEligible(r);
              const disabledReason = !eligible
                ? t('admin.csv_only_completed_paid') || 'CSV対象は「完了済み」かつ「支払済み」のみ'
                : '';

              return (
                <tr key={r.id} className="border-t hover:bg-gray-50 text-center align-middle">
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={eligible && selectedIds.has(r.id)}
                      disabled={!eligible}
                      onChange={() => toggleSelect(r)}
                      className="w-4 h-4 disabled:opacity-40"
                      title={disabledReason}
                    />
                  </td>

                  <td className="px-2 py-2">
                    <div className="flex items-center justify-center gap-1.5 mb-0.5">
                      <div className="font-semibold truncate max-w-[120px]" title={r.user_name || '-'}>
                        {r.user_name || '-'}
                      </div>
                      {renderProviderIcon(r.user_provider)}
                    </div>
                    <div className="text-coral font-medium text-xs truncate" title={karte ? `${t('doctor.chart_number') || 'カルテ番号'}: ${karte}` : ''}>
                      {karte || '-'}
                    </div>
                  </td>

                  <td className="px-2 py-2 whitespace-nowrap">
                    {t(`service.${r.service}`, r.service)}
                    {r.visit_type && (
                      <span className="ml-1 text-gray-500 text-xs">({t(`visit_type.${r.visit_type}`)})</span>
                    )}
                  </td>

                  <td className="px-2 py-2 whitespace-nowrap">{r.date}</td>
                  <td className={`px-2 py-2 whitespace-nowrap ${r.is_overdue ? 'text-red-600 font-bold' : ''}`}>{r.time}</td>

                  <td className="px-2 py-2 whitespace-nowrap">{renderStatusLabel(r)}</td>
                  <td className="px-2 py-2 whitespace-nowrap">{renderTranslationNeedText(r)}</td>
                  <td className="px-2 py-2">{renderPayment(r)}</td>

                  <td className="px-2 py-2">
                    {r.questionnaire_completed ? (
                      <span className="inline-flex items-center gap-1 text-green-600 font-semibold">✓ {t('doctor.completed')}</span>
                    ) : (
                      <span className="text-gray-400">{t('doctor.not_completed')}</span>
                    )}
                  </td>

                  <td className="px-2 py-2">
                    <button
                      onClick={() => {
                        setProfileData(r);
                        setShowProfileModal(true);
                      }}
                      className="text-coral underline"
                    >
                      {t('doctor.view_profile') || 'プロフィール'}
                    </button>
                  </td>

                  <td className="px-2 py-2">
                    <button onClick={() => openRecordView(r)} className="text-coral underline">
                      {t('doctor.view')}
                    </button>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      {canComplete && (
                        <button
                          onClick={() => adminMarkCompleted(r.id)}
                          className="px-4 py-1.5 rounded-full border border-emerald-200 text-xs md:text-sm font-bold whitespace-nowrap bg-white text-emerald-700 hover:bg-emerald-50 shadow-soft transition-all active:scale-95"
                          title={t('admin.mark_completed') || '診察完了にする'}
                        >
                          {t('admin.mark_completed') || '診察完了にする'}
                        </button>
                      )}
                      {canCancel && (
                        <button
                          onClick={() => adminCancel(r.id, r.status)}
                          className="px-4 py-1.5 rounded-full border border-red-200 text-xs md:text-sm font-bold whitespace-nowrap bg-white text-red-600 hover:bg-red-50 shadow-soft transition-all active:scale-95"
                          title={t('admin.cancel_by_admin') || '管理者キャンセル'}
                        >
                          {t('admin.cancel_by_admin') || '管理者キャンセル'}
                        </button>
                      )}
                      {(r.status === 'completed' && r.payment_status === 'paid') && (
                        <button
                          onClick={() => openTrackingModal(r)}
                          className={`px-4 py-1.5 rounded-full border text-xs md:text-sm font-bold whitespace-nowrap shadow-soft transition-all active:scale-95 ${r.shipping_status === 'shipped'
                            ? 'text-emerald-700 border-emerald-200 bg-white hover:bg-emerald-50'
                            : 'text-coral border-coral/30 bg-white hover:bg-coral/5'
                            }`}
                          title={t('admin.shipping_edit') || '配送信息'}
                        >
                          {r.shipping_status === 'shipped'
                            ? t('admin.shipped') || '発送済'
                            : t('admin.shipping_input_tracking') || '配送入力'}
                        </button>
                      )}
                      {/* Removed large payment copy button as it is now a small icon in the payment column */}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="hidden md:flex justify-center items-center gap-3 mt-4 pb-2">
            <button
              aria-label={t('prev') || 'Prev'}
              disabled={currentPage === 1}
              onClick={() =>
                setPageByGroup({
                  ...pageByGroup,
                  [group]: Math.max(1, currentPage - 1),
                })
              }
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 disabled:opacity-50 hover:bg-gray-300"
              title={t('prev') || 'Prev'}
            >
              ‹
            </button>
            <span className="text-sm">
              {currentPage} / {totalPages}
            </span>
            <button
              aria-label={t('next') || 'Next'}
              disabled={currentPage === totalPages}
              onClick={() =>
                setPageByGroup({
                  ...pageByGroup,
                  [group]: Math.min(totalPages, currentPage + 1),
                })
              }
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 disabled:opacity-50 hover:bg-gray-300"
              title={t('next') || 'Next'}
            >
              ›
            </button>
          </div>
        )}
      </>
    );
  };

  const renderMobileCards = (group: string, list: Reservation[]) => {
    const eligibleIdsInGroup = list.filter(isCsvEligible).map((r) => r.id);
    const selectedEligibleInGroupCount = eligibleIdsInGroup.filter((id) => selectedIds.has(id)).length;
    const allEligibleInGroupSelected =
      eligibleIdsInGroup.length > 0 && selectedEligibleInGroupCount === eligibleIdsInGroup.length;

    return (
      <div className="md:hidden space-y-3">
        {/* ✅ Mobile：全選択（グループ内の完了&支払済のみ） */}
        {eligibleIdsInGroup.length > 0 && (
          <div className="flex items-center justify-between bg-gray-50 border rounded-xl px-3 py-2">
            <div className="text-sm text-gray-700 font-semibold">
              {t('admin.select_all_eligible') || 'CSV対象を全選択'}
              <span className="ml-2 text-xs text-gray-500">
                ({selectedEligibleInGroupCount}/{eligibleIdsInGroup.length})
              </span>
            </div>
            <input
              type="checkbox"
              checked={allEligibleInGroupSelected}
              onChange={(e) => toggleSelectAllIds(eligibleIdsInGroup, e.target.checked)}
              className="w-4 h-4"
            />
          </div>
        )}

        {list.map((r) => {
          const canCancel = !['canceled', 'doctor_cancelled', 'admin_cancelled'].includes(r.status) && r.payment_status !== 'paid';
          const canComplete = r.status !== 'completed';
          const karte = getKarteNumber(r);

          const eligible = isCsvEligible(r);

          return (
            <div key={r.id} className="bg-white rounded-xl shadow p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={eligible && selectedIds.has(r.id)}
                      disabled={!eligible}
                      onChange={() => toggleSelect(r)}
                      className="w-4 h-4 mt-1 disabled:opacity-40"
                      title={!eligible ? t('admin.csv_only_completed_paid') || '完了&支払済のみ選択できます' : ''}
                    />
                    <div className="min-w-0">
                      <div className="font-semibold text-base truncate" title={r.user_name || '-'}>
                        {r.user_name || '-'}
                      </div>
                      {karte && (
                        <div className="text-gray-500 text-xs truncate" title={karte}>
                          {karte}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right text-xs text-gray-600 shrink-0">
                  <div>{r.date}</div>
                  <div>{r.time}</div>
                </div>
              </div>

              <div className="text-sm mt-2">
                {t(`service.${r.service}`, r.service)}
                {r.visit_type && <span className="ml-1 text-gray-500 text-xs">({t(`visit_type.${r.visit_type}`)})</span>}
              </div>

              <div className="mt-2 text-xs">{renderStatusLabel(r)}</div>
              <div className="text-xs text-gray-700">
                {(t('reserve.translation_col_header') || '翻译需求') + '：'}
                {renderTranslationNeedText(r)}
              </div>
              <div className="text-xs">{renderPayment(r)}</div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <button
                  onClick={() => {
                    setProfileData(r);
                    setShowProfileModal(true);
                  }}
                  className="px-3 py-2 border rounded hover:bg-gray-50"
                >
                  {t('doctor.profile')}
                </button>

                {(r.status === 'completed' && r.payment_status === 'paid') && (
                  <div className={`flex items-center justify-center border rounded hover:bg-gray-50 ${r.shipping_status === 'shipped' ? 'bg-emerald-50 border-emerald-200' : ''
                    }`}>
                    {r.shipping_status === 'shipped' ? (
                      <div className="text-center">
                        <span className="text-emerald-800 text-xs font-bold mr-1">{t('admin.shipped') || '発'}</span>
                        <a
                          href={(() => {
                            // 番号のハイフン/空白を除去し、carrier ごとに正しい照会URLへ（yupack を正とし旧 yu_pack も許容）
                            const n = (r.tracking_number || '').replace(/[^0-9A-Za-z]/g, '');
                            const c = r.shipping_carrier;
                            return c === 'sagawa'
                              ? `https://k2k.sagawa-exp.co.jp/p/web/okurijosearch.do?okurijoNo=${n}`
                              : (c === 'yupack' || c === 'yu_pack')
                                ? `https://trackings.post.japanpost.jp/services/srv/search/direct?searchKind=S004&locale=ja&reqCodeNo1=${n}`
                                : `https://toi.kuronekoyamato.co.jp/cgi-bin/tneko?number01=${n}`;
                          })()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-700 underline text-xs font-medium"
                        >
                          {r.tracking_number || ''}
                        </a>
                        <button onClick={() => openTrackingModal(r)} className="ml-2 text-xs text-gray-400">
                          ✏️
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => openTrackingModal(r)} className="w-full h-full text-xs text-coral">
                        {t('admin.shipping_input_tracking')}
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-3 flex gap-2 items-center">
                {canComplete && (
                  <button
                    onClick={() => adminMarkCompleted(r.id)}
                    className="flex-1 bg-emerald-600 text-white px-4 py-2 rounded text-sm hover:bg-emerald-700"
                  >
                    {t('admin.mark_completed') || '診察完了にする'}
                  </button>
                )}
                {canCancel && (
                  <button onClick={() => adminCancel(r.id, r.status)} className="flex-1 bg-red-500 text-white px-4 py-2 rounded text-sm hover:bg-red-600">
                    {t('admin.cancel_by_admin') || '管理者キャンセル'}
                  </button>
                )}
                {(r.zoom_join_url || (r.status !== 'completed' && r.payment_status !== 'paid')) && (
                  <button
                    onClick={() => toggleActionRow(r.id)}
                    className={`p-2 rounded border border-gray-200 text-gray-400 hover:bg-gray-50 flex-shrink-0 transition-all ${expandedActionRows[r.id] ? 'bg-gray-100 rotate-180' : ''}`}
                  >
                    <ChevronDown className="w-5 h-5" />
                  </button>
                )}
              </div>

              {expandedActionRows[r.id] && (r.zoom_join_url || (r.status !== 'completed' && r.payment_status !== 'paid')) && (
                <div className="mt-2 flex gap-2">
                  {r.zoom_join_url && (
                    <div className="flex-1 flex flex-col gap-1">
                      {r.zoom_meeting_id && (
                        <div className="text-[10px] text-gray-500 font-mono text-center">ID: {r.zoom_meeting_id}</div>
                      )}
                      <button
                        onClick={() => handleCopyLink(r.zoom_join_url!, t('zoom_join_url') || 'Zoomリンク')}
                        className="w-full border border-blue-200 text-blue-600 rounded bg-white hover:bg-blue-50 py-1.5 flex items-center justify-center gap-1 text-xs font-bold shadow-soft transition-colors"
                      >
                        <Video className="w-3.5 h-3.5" />
                        Zoom
                      </button>
                    </div>
                  )}
                  {r.status !== 'completed' && r.payment_status !== 'paid' && (
                    <button
                      onClick={() => handleCopyLink(`${window.location.origin}/payment/start/${r.id}`, t('payment.link') || '決済リンク')}
                      className="flex-1 border border-orange-200 text-orange-600 rounded bg-white hover:bg-orange-50 py-1.5 flex items-center justify-center gap-1 text-xs font-bold shadow-soft transition-colors"
                    >
                      <CreditCard className="w-3.5 h-3.5" />
                      {t('admin.btn_payment_link') || '決済'}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderGroupBlock = (group: string, list: Reservation[]) => (
    <>
      {renderMobileCards(group, list)}
      {renderDesktopTable(group, list)}
    </>
  );

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h2 className="text-2xl font-bold">{t('admin.reservation_list') || '予約一覧'}</h2>

        <div className="flex flex-wrap items-center gap-3">
          {/* ✅ CSV出力ボタン（対象選択中のみ表示） */}
          {selectedEligibleCount > 0 && (
            <button
              onClick={openCsvModal}
              className="px-4 py-2 bg-purple-600 text-white rounded shadow hover:bg-purple-700 text-sm font-semibold"
              title={t('admin.csv_only_completed_paid') || '完了&支払済のみCSV出力できます'}
            >
              {t('admin.generate_csv') || 'CSV出力 (ヤマト)'} ({selectedEligibleCount})
            </button>
          )}

          <button
            onClick={fetchReservations}
            disabled={loading}
            className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm"
            title={t('refresh') || '更新'}
          >
            {loading ? t('processing') || '処理中...' : t('refresh') || '更新'}
          </button>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t('common.page_size')}</span>
            <select
              aria-label="Page size"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageByGroup({});
              }}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setSortOrder('desc')}
              className={`px-5 py-2 rounded-full text-sm font-bold shadow-soft transition-all active:scale-95 ${sortOrder === 'desc' ? 'bg-coral text-white shadow-card' : 'bg-white border border-border text-gray-500 hover:bg-gray-50'
                }`}
              title={t('sort.desc') || '新しい順'}
            >
              {t('sort.desc') || '新しい順'}
            </button>
            <button
              onClick={() => setSortOrder('asc')}
              className={`px-5 py-2 rounded-full text-sm font-bold shadow-soft transition-all active:scale-95 ${sortOrder === 'asc' ? 'bg-coral text-white shadow-card' : 'bg-white border border-border text-gray-500 hover:bg-gray-50'
                }`}
              title={t('sort.asc') || '古い順'}
            >
              {t('sort.asc') || '古い順'}
            </button>
          </div>
        </div>
      </div>

      {(['pool', 'assigned', 'completed', 'canceled', 'expired'] as const).map((groupKey) => {
        const list = grouped[groupKey] || [];
        const title =
          groupKey === 'pool'
            ? t('doctor.pool') || '予約プール（未割当）'
            : groupKey === 'assigned'
              ? t('doctor.assigned') || '診療待ち（割当済み）'
              : groupKey === 'completed'
                ? t('doctor.completed')
                : groupKey === 'canceled'
                  ? t('doctor.cancelled')
                  : t('mypage.expired');

        return (
          <div key={groupKey} className="mb-6 bg-white rounded-xl shadow-lg">
            <div
              className="flex justify-between items-center cursor-pointer bg-gray-200 px-4 h-12 rounded-t-xl"
              onClick={() =>
                setCollapsedGroups((p) => ({
                  ...p,
                  [groupKey]: !p[groupKey],
                }))
              }
            >
              <span className="font-semibold text-lg">
                {title} ({list.length})
              </span>
              <span>{collapsedGroups[groupKey] ? '+' : '-'}</span>
            </div>
            {!collapsedGroups[groupKey] && <div className="p-3">{renderGroupBlock(groupKey, list)}</div>}
          </div>
        );
      })}

      {/* プロフィールモーダル（閲覧専用） */}
      {showProfileModal && profileData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-96 max-w-[95vw] shadow-lg">
            <h2 className="text-lg font-bold">{t('profile.patient_info')}</h2>
            <div className="text-sm space-y-1 mt-3">
              <div>
                {t('doctor.name')}: {profileData.user_profile?.full_name || t('na')}
              </div>
              <div>
                {t('doctor.furigana')}: {profileData.user_profile?.furigana || t('na')}
              </div>
              <div>
                {t('doctor.gender')}: {profileData.user_profile?.gender || t('na')}
              </div>
              <div>
                {t('doctor.birthday')}: {profileData.user_profile?.birth_date || t('na')}
              </div>
              <div>
                {t('doctor.phone')}: {profileData.user_profile?.phone_number || t('na')}
              </div>
              <div>
                {t('doctor.postal_code')}: {profileData.user_profile?.postal_code || t('na')}
              </div>
              <div>
                {t('doctor.address')}: {profileData.user_profile?.address || t('na')}
              </div>
              <div>
                {t('doctor.karte_number') || 'カルテ番号'}: {getKarteNumber(profileData) || t('na')}
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowProfileModal(false)} className="px-4 py-2 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap hover:bg-coral">
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV生成モーダル */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-80 shadow-lg">
            <h3 className="text-lg font-bold mb-2">{t('admin.csv_export_title') || 'ヤマトB2CSV出力'}</h3>
            <div className="text-xs text-gray-600 mb-4">
              {t('admin.csv_only_completed_paid') || '※ CSV出力は「完了済み」かつ「支払済み」のみ対象です'}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">{t('admin.csv_cool_type') || '配送タイプ'}</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="coolType" value={0} checked={csvCoolType === 0} onChange={() => setCsvCoolType(0)} />
                  <span>{t('admin.csv_normal') || '普通配送 (発払い)'}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="coolType" value={1} checked={csvCoolType === 1} onChange={() => setCsvCoolType(1)} />
                  <span>{t('admin.csv_cool') || 'クール便 (冷蔵)'}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="coolType" value={2} checked={csvCoolType === 2} onChange={() => setCsvCoolType(2)} />
                  <span>{t('admin.csv_frozen') || 'クール便 (冷凍)'}</span>
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">{t('admin.csv_ship_date') || '出荷予定日'}</label>
              <input
                type="date"
                value={csvShipDate}
                onChange={(e) => setCsvShipDate(e.target.value)}
                className="w-full border rounded p-2 text-sm"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">{t('admin.csv_sender_select') || '発送元住所'}</label>
              <select
                value={selectedSenderId || ''}
                onChange={(e) => setSelectedSenderId(Number(e.target.value) || undefined)}
                className="w-full border rounded p-2 text-sm"
              >
                <option value="">{t('admin.csv_sender_default') || '未選択（デフォルト）'}</option>
                {senderAddresses.map((sa) => (
                  <option key={sa.id} value={sa.id}>
                    {sa.name} ({sa.address})
                  </option>
                ))}
              </select>
              <div className="mt-1 text-right">
                <button
                  onClick={() => navigate('/admin/sender-addresses')}
                  className="text-xs text-coral hover:underline"
                >
                  {t('admin.address_management') || '住所管理'}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowCsvModal(false)} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded">
                {t('common.cancel')}
              </button>
              <button
                onClick={handleGenerateCsv}
                disabled={selectedEligibleCount === 0}
                className="px-4 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
              >
                {t('admin.download') || 'ダウンロード'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 追跡番号入力モーダル */}
      {showTrackingModal && trackingTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-80 shadow-lg">
            <h3 className="text-lg font-bold mb-4">{t('admin.tracking_input_title') || '追跡番号の登録'}</h3>
            <div className="text-sm text-gray-600 mb-2">
              ID: {trackingTarget.id} <br />
              {trackingTarget.user_name} 様 <br />
              {trackingTarget.shipping_status === 'shipped' && (
                <span className="text-coral font-bold">
                  {t('admin.shipped') || '発送済'} (Current: {trackingTarget.tracking_number})
                </span>
              )}
            </div>

            <input
              type="text"
              value={trackingInput}
              onChange={(e) => setTrackingInput(e.target.value)}
              placeholder={t('admin.tracking_input_placeholder') || '送り状番号 (半角数字)'}
              className="w-full border rounded p-2 mb-3"
            />

            {/* 配送業者の選択（照会URLの判定にも使うため必須。配送管理画面と同一の選択肢） */}
            <label className="block text-xs font-bold text-gray-500 mb-1">{t('admin.shipping_carrier') || '配送業者'}</label>
            <select
              value={trackingCarrier}
              onChange={(e) => setTrackingCarrier(e.target.value)}
              className="w-full border rounded p-2 mb-4 bg-white"
            >
              <option value="yamato">{t('admin.carrier_yamato') || 'ヤマト運輸'}</option>
              <option value="sagawa">{t('admin.carrier_sagawa') || '佐川急便'}</option>
              <option value="yupack">{t('admin.carrier_yu_pack') || '日本郵便'}</option>
            </select>

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowTrackingModal(false)} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded">
                {t('common.cancel')}
              </button>
              <button onClick={saveTrackingNumber} className="px-4 py-1.5 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap">
                {t('admin.tracking_save') || '登録する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 診療記録モーダル（閲覧専用） */}
      {showRecordModal && recordTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-[680px] max-w-[95vw] shadow-lg">
            <h2 className="text-lg font-bold">
              {t('doctor.medical_record')} — {t(`service.${recordTarget.service}`)}{' '}
              {recordTarget.visit_type ? `(${t(`visit_type.${recordTarget.visit_type}`)})` : ''}
            </h2>

            <div className="grid grid-cols-2 gap-3 text-sm mb-3 mt-3">
              <div>
                <span className="text-gray-500">{t('doctor.date')}:</span> {recordTarget.date}
              </div>
              <div>
                <span className="text-gray-500">{t('doctor.time')}:</span> {recordTarget.time}
              </div>
              <div>
                <span className="text-gray-500">{t('doctor.patient')}:</span> {recordTarget.user_name || '-'}
              </div>
              <div>
                <span className="text-gray-500">{t('doctor.karte_number') || 'カルテ番号'}:</span> {getKarteNumber(recordTarget) || '-'}
              </div>
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <div className="text-gray-500 mb-1">{t('doctor.questionnaire')}</div>
                <div className="border rounded p-2 bg-gray-50">
                  {recordTarget.questionnaire_completed ? t('doctor.completed') : t('doctor.not_completed')}
                </div>
              </div>

              <div>
                <div className="text-gray-500 mb-1">{t('doctor.diagnosis') || '診断内容'}</div>
                <div className="border rounded p-2 bg-gray-50 whitespace-pre-wrap">{recordTarget.diagnosis || '-'}</div>
              </div>

              <div>
                <div className="text-gray-500 mb-1">{t('doctor.prescription') || '処方箋'}</div>
                <div className="border rounded p-2 bg-gray-50 whitespace-pre-wrap">{recordTarget.prescription_text || '-'}</div>
              </div>

              <div>
                <div className="text-gray-500 mb-1">{t('doctor.notes') || 'メモ'}</div>
                <div className="border rounded p-2 bg-gray-50 whitespace-pre-wrap">{recordTarget.notes || '-'}</div>
              </div>
            </div>

            <div className="flex justify-end mt-5 gap-2">
              <button onClick={() => setShowRecordModal(false)} className="px-4 py-2 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap hover:bg-coral">
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReservations;
